import React, {forwardRef, createRef, useState, useEffect, useRef, useLayoutEffect, useCallback} from 'react';
import {View, StyleSheet, Switch, ScrollView} from 'react-native';
import {Picker} from '@react-native-community/picker';


import AppText from './AppText';
import {inputStyles} from '../styles/input';
import {basicStyles} from '../styles/basic';
import ChevronDownSvg from './svgs/ChevronDownSvg';
import {AVATAR_DEFAULT} from '../config';
import Avatar from './Avatar';
import StatusIndicator from './StatusIndicator';



const AppMultiPickerInput = forwardRef(({
	                                   label,
	                                   selectedValue,
	                                   onValueChange,
	                                   title = null,
	                                   labelExtractor = 'label',
	                                   valueExtractor = 'value',
	                                   iconExtractor = null,
	                                   iconRoot = null,
	                                   items = [],
	                                   wrapperStyle = {},
	                                   labelStyle = {},
	                                   showLabel = true,
	                                   naked = false,
	                                   nextInputRef = null,
	                                   statusColor = null,
	                                   arrowBordered = true,
	                                   nullable = false,
	                                   ...rest
                                   },
                                   ref,
) => {
	const [isFocused, setIsFocused] = useState(false);
	const [active, setActive] = useState(false);
	if (!ref) {
		ref = createRef();
	}
	const wrapperRef = useRef(null);

	const getSelectedLabel = () => {
		let selectedValues = selectedValue.map((value) => getItemValue(value));
		let selected = items.filter(item => {
			return selectedValues.includes(getItemValue(item));
		});

		if (selected.length > 0) {
			return selected.map(item => getItemLabel(item)).join();
		}

		return 'Select ' + (title || label); // return space to keep height
	};

	const getSelectedIcon = () => {

		if (iconRoot) {
			let selected = items.find(item => {
				return item[valueExtractor] === selectedValue;
			});

			if (selected) {
				return iconRoot + selected[iconExtractor];
			}
		}

		return AVATAR_DEFAULT;
	};



	const getItemLabel = (item) => {
		if (typeof labelExtractor === 'function') {
			return labelExtractor(item);
		}

		return item[labelExtractor];
	};

	const getItemValue = (item) => {
		if (typeof valueExtractor === 'function') {
			return valueExtractor(item);
		}

		return item[valueExtractor];
	};

	const handleValueChange = (item) => {
		if (selectedValue.map((value) => getItemValue(value)).includes(getItemValue(item))) {
			const newSelectedValue = selectedValue;
			const itemIndex = newSelectedValue.findIndex(store_front => {
				return getItemValue(store_front) === getItemValue(item);
			});
			newSelectedValue.splice(itemIndex, 1);
			onValueChange(newSelectedValue);
		} else {
			onValueChange([
				...selectedValue,
				item
			]);
		}
	};



	const useClickOutside = (
		node = null,
		onClickOutside = () => {},
		useCapture = false,
	) => {
		const onClick = useCallback(
			(e) => {
				if (node && !node.contains(e.target)) {
					onClickOutside();
				}
			},
			[node, onClickOutside],
		);

		useLayoutEffect(() => {
			document.addEventListener('click', onClick, useCapture);

			return () => {
				document.removeEventListener('click', onClick, useCapture);
			};
		}, [onClick, useCapture]);
	};

	useClickOutside(wrapperRef.current, () => {
		if(active) {
			setActive(false);
		}
	});

	return (

		<View style={naked ? [wrapperStyle] : [
			inputStyles.inputWrapper,
			{
				paddingRight: 35,
				...(iconExtractor ? {paddingLeft: 123} : {}),
				...(statusColor ? {paddingLeft: 35} : {}),
				...(showLabel ? {} : {justifyContent: 'center'}),
			},
			wrapperStyle,
			...(isFocused ? [styles.focused] : []),
		]}
		      onClick={() => setActive(true)}
		      onPress={() => setActive(true)}
		>
			{
				iconExtractor ?
					<View style={[
						basicStyles.flexCenterContent,
						{
							width: 108,
							height: '100%',
							position: 'absolute',
							top: 0,
							left: 0,
							bottom: 0,
							borderRightWidth: 1,
							borderRightColor: '#E2E9F4',
						},
					]}>
						<Avatar
							source={{uri: getSelectedIcon()}}
							width={66}
						/>
					</View>
					: null
			}
			{
				statusColor ?
					<View style={[
						basicStyles.flexCenterContent,
						{
							width: 45,
							height: '100%',
							position: 'absolute',
							top: 0,
							left: 0,
							bottom: 0,
						},
					]}>
						<StatusIndicator color={statusColor}/>
					</View>
					: null
			}
			<View>
				{
					showLabel ?
						<AppText style={[inputStyles.inputLabel, labelStyle]}>
							{label}
						</AppText>
						: null
				}
				<AppText style={[inputStyles.input, {textWrap: 'nowrap'}]}>
					{getSelectedLabel()}
				</AppText>
			</View>
			{
				naked ? null :
					<View style={[
						basicStyles.flexCenterContent,
						{
							width: 25,
							height: '100%',
							position: 'absolute',
							top: 0,
							right: 0,
							bottom: 0,
							borderLeftWidth: arrowBordered ? 1 : 0,
							borderLeftColor: '#E2E9F4',
						},
					]}>
						<ChevronDownSvg color="#0065DB" onFocus={() => ref.current.focus()}/>
					</View>
			}


			<div
				ref={wrapperRef}
				onClick={(e) => {
					e.stopPropagation();
				}}
				style={
					{
						position: 'absolute',
						top: '100%',
						width: '100%',
						left: 0,
						paddingTop: 10,
						display: active && items.length > 0 ? 'flex' : 'none',
						flexDirection: 'column'
					}
				}>
				<View style={[
					basicStyles.contentSection,
				]}>
					<View style={[
						{
							padding: 15
						},
					]}>
						<ScrollView>
							{
								items.map(item => {
									return (
										<View
											key={getItemValue(item)}
											style={[
												basicStyles.flexRow,
												basicStyles.alignContentCenter,
												{width: '100%', marginTop: 5, marginBottom: 5},
											]}
										>
											<Switch
												style={{marginRight: 15}}
												value={selectedValue.map((value) => getItemValue(value)).includes(getItemValue(item))}
												onValueChange={() => handleValueChange(item)}
												trackColor={{
													true: '#0065DB',
													false: '#F2F2F2',
												}}
												thumbColor="#0065DB"
												activeThumbColor="#FFFFFF"
											/>
											<AppText numberOfLines={1}>
												{getItemLabel(item)}
											</AppText>
										</View>
									);
								})
							}
						</ScrollView>
					</View>
				</View>
			</div>
		</View>
	);
});

export default AppMultiPickerInput;


const styles = StyleSheet.create({
	focused: {
		borderWidth: 1,
		borderColor: '#4D90FE',
	},
	selectedStyle: {
		borderRadius: 12,
	},
});